import { Attention } from "@warp-ds/elements/react/attention";
import { Button } from "@warp-ds/elements/react/button";
import { useRef, useState } from "react";
import { Icon } from "./Icon.tsx";

export type Placement = "top" | "right" | "bottom" | "left";

/** Fallbacks tried, in order, when the preferred placement does not fit in the viewport */
const FALLBACK_PLACEMENTS: Record<Placement, Placement[]> = {
  top: ["bottom", "right", "left"],
  right: ["left", "top", "bottom"],
  bottom: ["top", "left", "right"],
  left: ["right", "top", "bottom"],
};

type Props = {
  tooltip: string;
  /** The text the icon follows. Only used to give the tooltip a readable element id. */
  label: string;
  /** Where the tooltip opens relative to the info icon. Defaults to "right". */
  placement?: Placement;
  /** Called the first time the tooltip opens. Opt-in, so untracked tooltips need no pulse context. */
  onOpen?: () => void;
};

/** An info icon that explains the text it follows. Inline, so it goes inside the label, not beside it. */
export const Tooltip = ({ tooltip, label, placement = "right", onOpen }: Props) => {
  const targetEl = useRef(null);
  const [showTooltip, setShowtoolTip] = useState(false);
  // A mouse opens the tooltip twice — on enter, then on the focus a click brings — so only the
  // first open is reported
  const hasReportedOpen = useRef(false);
  const tooltipId = `tooltip-${label
    .replace(/[()[\]{}]/g, "")
    .replace(/\s/g, "-")
    .toLowerCase()}`;

  const open = () => {
    setShowtoolTip(true);
    if (hasReportedOpen.current) {
      return;
    }
    hasReportedOpen.current = true;
    onOpen?.();
  };
  const close = () => setShowtoolTip(false);

  return (
    <span className="inline-flex ml-4">
      <Attention
        tooltip
        flip
        fallbackPlacements={FALLBACK_PLACEMENTS[placement]}
        placement={placement}
        show={showTooltip}
        className="sm:max-w-screen-sm whitespace-normal"
      >
        <Button
          slot="target"
          variant="link"
          className="align-sub"
          ref={targetEl}
          aria-describedby={tooltipId}
          // A string, not a boolean: React writes `aria-expanded=""` on later updates to a custom element.
          aria-expanded={showTooltip ? "true" : "false"}
          onMouseEnter={open}
          onMouseLeave={close}
          onFocus={open}
          onBlur={close}
        >
          <Icon name="Info" />
        </Button>

        <p slot="message" className="md:max-w-[300]" id={tooltipId}>
          {tooltip}
        </p>
      </Attention>
    </span>
  );
};
